import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetWishListsApiParameter } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import updateWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/UpdateWishList";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardList from "@insite/content-library/Components/CardList";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MyListCardContainer from "@tCentro/Components/MyListCardContainer";
import MyListCardListHeading from "@tCentro/Components/MyListCartListHeading";
import MyListWishListCard from "@tCentro/Components/MyListWishListCard";
import CENArrowRightLong from "@tCentro/Icons/CENArrowRightLong";
import { COLORS } from "@tCentro/theme/design";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    backgroundColor = "backgroundColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.backgroundColor]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    return {
        recentWishListsDataView: getWishListsDataView(state, recentWishListsParameter),
        myListsPageNavLink: getPageLinkByPageType(state, "MyListsPage"),
        canViewWishLists: (session.isAuthenticated || session.rememberMe) && !session.isGuest,
    };
};

let recentWishListsParameter = {
    page: 1,
    sort: "ModifiedOn DESC",
    expand: ["top3products", "favorite", "hidden", "tags"],
} as GetWishListsApiParameter;

const mapDispatchToProps = {
    loadWishLists,
    addWishListToCart,
    updateWishList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentWishListsStyles {
    wishListCardGridItem?: GridItemProps;
    noWishListGridItem?: GridItemProps;
    noWishListsText?: TypographyProps;
    cardsContainer?: InjectableCss;
    mainContainer?: InjectableCss;
    iconOne?: IconPresentationProps;
    createListLinkStyles?: InjectableCss;
}

export const recentWishListsStyles: RecentWishListsStyles = {
    wishListCardGridItem: {
        width: 12,
        css: css`
            padding: 0px 4px;
        `,
    },
    noWishListGridItem: {
        width: 12,
        css: css`
            background-color: #f2f2f2;
            margin: 32px 40px 0px 40px !important;
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: column;
            min-height: 148px;
        `,
    },
    cardsContainer: {
        css: css`
            // padding-top: 8px;
            margin-top: 28px;
            max-height: 340px;
            width: 96%;
            margin-right: 24px;
            overflow-y: auto;
            overflow-x: hidden;
            display: flex;
            flex-direction: column;
            ::-webkit-scrollbar {
                width: 6px;
            }

            ::-webkit-scrollbar-thumb {
                background-color: #ccc;
                border-radius: 3px;
            }

            ::-webkit-scrollbar-thumb:hover {
                background-color: #999;
            }
        `,
    },
    mainContainer: {
        css: css`
            border: 4px solid ${COLORS.background.grayShade};
            padding-bottom: 21px;
        `,
    },
    noWishListsText: {
        css: css`
            p:nth-of-type(1) {
                font-size: 14px;
                line-height: 16px;
                font-weight: 600;
                text-align: center;
                margin-bottom: 4px;
                color: ${COLORS.text.base};
            }
            p:nth-of-type(2) {
                font-size: 14px;
                line-height: 24px;
                font-weight: 400;
                text-align: center;
                margin-bottom: 16px;
                color: ${COLORS.text.base};
            }
        `,
    },
    iconOne: {
        size: 16,
        color: `${getColor("text.link")} !important`,
        css: css`
            svg {
                > path {
                    fill: ${getColor("text.link")} !important;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 12px;
                        height: 12px;
                    `,
                    css`
                        width: 12px;
                        height: 12px;
                    `,
                    css`
                        width: 14px;
                        height: 14px;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    createListLinkStyles: {
        css: css`
            display: flex;
            font-size: 14px;
            line-height: 16px;
            font-weight: 600;
            a {
                span {
                    display: flex;
                    align-items: center;
                    font-weight: 600;
                    font-size: 14px;
                    line-height: 16px;
                }
            }
        `,
    },
};

const styles = recentWishListsStyles;

class RecentWishLists extends React.Component<Props> {
    componentDidMount() {
        recentWishListsParameter.pageSize = this.props.fields.numberOfRecords;
        if (
            this.props.canViewWishLists &&
            !this.props.recentWishListsDataView.value &&
            !this.props.recentWishListsDataView.isLoading
        ) {
            this.props.loadWishLists(recentWishListsParameter);
        }
    }

    componentDidUpdate() {
        if (
            this.props.canViewWishLists &&
            !this.props.recentWishListsDataView.value &&
            !this.props.recentWishListsDataView.isLoading
        ) {
            this.props.loadWishLists(recentWishListsParameter);
        }
    }

    toggleFavoriteClickHandler = (wishList: WishListModel) => {
        if (!wishList) {
            return;
        }

        this.props.updateWishList({
            apiParameter: {
                wishList: {
                    ...wishList,
                    isFavorite: !wishList.isFavorite,
                    wishListLineCollection: null,
                },
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    this.onSuccess?.();
                } else if (resultProps.result?.errorMessage) {
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };

    toggleHideClickHandler = (wishList: WishListModel) => {
        if (!wishList) {
            return;
        }

        this.props.updateWishList({
            apiParameter: {
                wishList: {
                    ...wishList,
                    isHidden: !wishList.isHidden,
                    wishListLineCollection: null,
                },
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    this.onSuccess?.();
                } else if (resultProps.result?.errorMessage) {
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };

    render() {
        if (!this.props.canViewWishLists) {
            return null;
        }

        const {
            recentWishListsDataView: { value: recentWishLists },
            myListsPageNavLink,
            fields,
        } = this.props;
        const myListsPageUrl = myListsPageNavLink ? myListsPageNavLink.url : undefined;

        return (
            <>
                <StyledWrapper
                    css={css`
                        max-height: 16px;
                        height: 100%;
                        background: ${fields.backgroundColor};
                    `}
                ></StyledWrapper>
                <StyledWrapper {...styles.mainContainer}>
                    <CardList
                        css={css`
                            > div {
                                div:nth-child(1) {
                                    h2 {
                                        font-size: 18px;
                                        font-weight: 600;
                                        line-height: 24px;
                                        font-family: "Poppins";
                                        text-transform: uppercase;
                                    }
                                }
                            }
                        `}
                        data-test-selector="cardListRecentWishLists"
                    >
                        <MyListCardListHeading
                            heading={translate("My Lists")}
                            viewAllUrl={myListsPageUrl}
                            recentWishListsDataView={this.props.recentWishListsDataView}
                            myListsPageNavLink={myListsPageNavLink}
                            canViewWishLists={false}
                        />
                        {recentWishLists && recentWishLists.length === 0 && (
                            <GridItem {...styles.noWishListGridItem}>
                                <Typography {...styles.noWishListsText}>
                                    {siteMessage("WishLists_NoWishlistsFound")}
                                </Typography>
                                <StyledWrapper {...styles.createListLinkStyles}>
                                    <Link href={myListsPageNavLink?.url}>
                                        {translate("Create a List")}{" "}
                                        <Icon src={CENArrowRightLong} {...styles.iconOne} />
                                    </Link>
                                </StyledWrapper>
                            </GridItem>
                        )}
                        <StyledWrapper {...styles.cardsContainer}>
                            {" "}
                            {/* Enable scrollable container */}
                            {recentWishLists &&
                                recentWishLists.map(wishList => (
                                    <MyListCardContainer key={wishList.id}>
                                        <GridItem {...styles.wishListCardGridItem}>
                                            <MyListWishListCard
                                                wishList={wishList}
                                                toggleFavorite={() => this.toggleFavoriteClickHandler(wishList)}
                                                toggleHide={() => this.toggleHideClickHandler(wishList)}
                                            />
                                        </GridItem>
                                    </MyListCardContainer>
                                ))}
                        </StyledWrapper>
                    </CardList>
                </StyledWrapper>
            </>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentWishLists),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Lists Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 3,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.backgroundColor,
                editorTemplate: "TextField",
                defaultValue: "linear-gradient(90deg,#266b40 27.08%,#2d804c 71.87%,#43687a 100%)",
                fieldType: "General",
                displayName: "Background Color",
            },
        ],
    },
};

export default widgetModule;
